import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useMst } from '../model/Root';
import Layout from '../components/Layout';
import Button from '../components/Button/Button';
import TextInput from '../components/FormComponents/TextInput';
import { getSettings, updateSetting, resetAllUserUsage } from '../services/api';
import { Setting } from '../types';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { color } from '../theme';

const useStyles = makeStyles()({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minWidth: 700,
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '45%',
    maxWidth: 700,
    minWidth: 500,
    alignItems: 'flex-start',
  },
});

const SettingsScreen = observer(() => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const {
    errorStore: { setError, setSuccess },
  } = useMst();

  const [limit, setLimit] = useState<string>('');
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const settingKey = 'monthlyAudiobookLimitSeconds';

  const fetchSettings = useCallback(async () => {
    try {
      const response = await getSettings();
      const settings = response.data;
      const audiobookLimitSetting = settings.find((s) => s.key === settingKey);
      if (audiobookLimitSetting) {
        setLimit(String(audiobookLimitSetting.value ?? ''));
      }
    } catch (error) {
      setError(t('error_fetching_settings'));
    }
  }, [setError, t]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const onSave = async () => {
    // Capture the value at click-time to avoid overwriting later edits if the request races.
    const pendingValue = limit;
    try {
      const parsed = Number(pendingValue);
      if (!Number.isFinite(parsed) || parsed < 0) {
        setError(t('error_saving_settings'));
        return;
      }
      const normalizedNumber = Math.floor(parsed);
      const newSetting: Setting = { key: settingKey, value: normalizedNumber };
      await updateSetting(newSetting);
      setLimit(normalizedNumber.toString());
      setSuccess(t('settings.settings_saved'));
      // Re-fetch to show what backend persisted (in case it adjusts)
      try {
        await fetchSettings();
      } catch (err) {
        // Fetch failed after a successful save; inform separately without implying save failure.
        setError(t('error_fetching_settings'));
      }
    } catch (error) {
      setError(t('error_saving_settings'));
    }
  };

  const handleResetAllUsage = async () => {
    try {
      await resetAllUserUsage();
      setSuccess(t('user_reset_all_usage_success'));
    } catch (error) {
      setError(t('user_reset_all_usage_error'));
    }
    setOpenResetDialog(false);
  };

  const onChange = (value: string) => {
    setLimit(value);
  };

  return (
    <Layout title={t('settings.listening_limit_nav')}>
      <div className={classes.wrapper}>
        <div className={classes.contentContainer}>
          <h2>{t('settings.audiobook_limit_title')}</h2>
          <TextInput
            label={t('settings.audiobook_limit_label')}
            value={limit}
            type="number"
            placeholder={t('settings.monthly_audiobook_limit_seconds_placeholder')}
            inputType="number"
            onChange={(key: string, value: string) => onChange(value)}
            style={{ width: '90%' }}
          />
          <Button onClick={onSave} text={t('save')} />
        </div>
        {/* HIDDEN: Dangerous button that removes usage data instead of just resetting listening time */}
        {/* <div className={classes.contentContainer} style={{ marginTop: '2rem' }}>
          <h2>{t('settings.reset_all_usage_title')}</h2>
          <Button
            onClick={() => setOpenResetDialog(true)}
            text={t('settings.reset_all_usage_button')}
            backgroundColor={color.error}
          />
        </div> */}
      </div>
      <Dialog open={openResetDialog} onClose={() => setOpenResetDialog(false)}>
        <DialogTitle>{t('user_reset_listening_time_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('user_reset_all_usage_confirm')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResetDialog(false)} text={t('cancel')} />
          <Button onClick={handleResetAllUsage} text={t('reset')} backgroundColor={color.error} />
        </DialogActions>
      </Dialog>
    </Layout>
  );
});

export default SettingsScreen;
